import React, {TouchEvent, useEffect, useRef} from 'react';
import {photo} from "../../../models/photo";

export type LightboxProps = {
  photos: photo[],
  index: number,
  title?: string,
  change: (index: number) => void,
  close: () => void,
}

const SWIPE_THRESHOLD: number = 50;

export const Lightbox = ({photos, index, title, change, close}: LightboxProps) => {
  const touchStart = useRef(undefined as undefined | number);
  const thumbs = useRef<HTMLDivElement>(null);

  const total: number = photos.length;
  const current: photo | undefined = photos[index];

  const go = (step: number): void => {
    change((index + step + total) % total);
  }

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowLeft') change((index - 1 + total) % total);
      if (e.key === 'ArrowRight') change((index + 1) % total);
    };

    window.addEventListener('keydown', onKey);

    return () => window.removeEventListener('keydown', onKey);
  }, [index, total, change, close]);

  useEffect(() => {
    const overflow: string = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = overflow;
    };
  }, []);

  useEffect(() => {
    thumbs.current
      ?.querySelector('.__lightbox_thumb_active__')
      ?.scrollIntoView({behavior: 'smooth', inline: 'center', block: 'nearest'});
  }, [index]);

  const onTouchEnd = (e: TouchEvent<HTMLDivElement>): void => {
    if (touchStart.current === undefined) {
      return;
    }

    const delta: number = e.changedTouches[0].clientX - touchStart.current;
    touchStart.current = undefined;

    if (Math.abs(delta) > SWIPE_THRESHOLD) {
      go(delta < 0 ? 1 : -1);
    }
  }

  if (current === undefined) {
    return null;
  }

  return (
    <div className="__lightbox__" role="dialog" aria-modal="true">
      <div className="__lightbox_header__">
        <span className="text-truncate">{title}</span>
        <span className="ms-auto me-3 __lightbox_counter__">{index + 1} / {total}</span>
        <button className="__lightbox_btn__" onClick={close} aria-label="Fermer">
          <i className="fa-solid fa-xmark"/>
        </button>
      </div>

      <div className="__lightbox_stage__"
           onClick={(e) => e.target === e.currentTarget && close()}
           onTouchStart={(e) => touchStart.current = e.touches[0].clientX}
           onTouchEnd={onTouchEnd}>
        {total > 1 && (
          <button className="__lightbox_btn__ __lightbox_nav__ __lightbox_prev__" onClick={() => go(-1)} aria-label="Photo précédente">
            <i className="fa-solid fa-chevron-left"/>
          </button>
        )}

        <img key={current.photoId} src={current.src} alt={`${title ?? 'Photo'} ${index + 1}`} className="__lightbox_image__"/>

        {total > 1 && (
          <button className="__lightbox_btn__ __lightbox_nav__ __lightbox_next__" onClick={() => go(1)} aria-label="Photo suivante">
            <i className="fa-solid fa-chevron-right"/>
          </button>
        )}
      </div>

      {total > 1 && (
        <div className="__lightbox_thumbs__" ref={thumbs}>
          {photos.map((p: photo, i: number) => (
            <img key={p.photoId} src={p.src} alt=""
                 className={`__lightbox_thumb__ ${i === index ? '__lightbox_thumb_active__' : ''}`}
                 onClick={() => change(i)}/>
          ))}
        </div>
      )}
    </div>
  );
}
